/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from './xda-strings.service';
import { XdaTranslate } from '../pipes/xda-translate.pipe';

@Injectable({
  providedIn: 'root',
})
export class ActiveDocumentsService {
  rPostBaseUrl;

  private xdaTranslatePipe: XdaTranslate;
  constructor(private xdaStringsService: XdaStringsService) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.rPostBaseUrl = 'api/rpost/';
  }

  getRPostBaseUrl() {
    return this.rPostBaseUrl;
  }

  mapDocumentStatus(date: string): string {
    const expired = new Date(date).getTime(),
      today = new Date().getTime();
    if (expired < today) {
      return 'SDE_EXPIRED';
    } else {
      return 'SDE_ACTIVE';
    }
  }

  mapDocumentEnabled(date: string): boolean {
    const expired = new Date(date).getTime(),
      today = new Date().getTime();
    if (expired < today) {
      return false;
    } else {
      return true;
    }
  }

  mapSecurityLevel(distRestriction: boolean, readerEmail: boolean): string {
    if (distRestriction === false && readerEmail === false) {
      // level 1
      return 'SDE_TRACK_VIEWS';
    } else if (distRestriction === false && readerEmail === true) {
      // level 2
      return 'SDE_TRACK_VIEWERS';
    } else if (distRestriction === true && readerEmail === true) {
      // level 3
      return 'SDE_RESTRICT_TRACK_VIEWERS';
    }

    return '';
  }

  formatDate(date: string): string {
    const dateObj = new Date(date),
      local = window.navigator.language || 'en-US',
      formatedDate = new Intl.DateTimeFormat(local).format(dateObj);

    return formatedDate;
  }

  formatTime(date: string): string {
    const dateObj = new Date(date),
      local = window.navigator.language || 'en-US',
      options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
      },
      formatedTime = new Intl.DateTimeFormat(local, options).format(dateObj);

    return formatedTime;
  }

  convertToNgbDateStruct(date: string): NgbDateStruct {
    const dateObj = new Date(date);
    return {
      year: dateObj.getFullYear(),
      month: dateObj.getMonth() + 1,
      day: dateObj.getDate(),
    };
  }

  isDocumentExpiringSoon(d: ActiveDocType, interval: number): boolean {
    const now = new Date().setHours(0, 0, 0, 0);
    const expireDate = new Date(d.documentOptions.expiryTime).setHours(
      0,
      0,
      0,
      0,
    );

    // difference between dates
    const msDiff = Math.abs(expireDate - now);
    const dayDiff = msDiff / (24 * 60 * 60 * 1000);

    // if dayDiff is less than interval and the document is already expired return true and set documentExpiryToolTip for the document
    if (dayDiff <= interval && d.documentStatus != 'SDE_EXPIRED') {
      d.documentExpiryToolTip = `${this.xdaTranslatePipe.transform('SDE_DAYS_BEFORE_EXPIRATION')}: ${Math.floor(dayDiff)}`;
      return true;
    } else {
      return false;
    }
  }
}

export interface ActiveDocType {
  id: string;
  documentId: string;
  ownerId: string;
  fileName: string;
  senderAddress: string;
  documentOptions: DocumentOptions;
  inputFiles: InputFile[];
  distributions: Distribution[];
  openRecords: OpenRecord[];
  data: null;
  rpd: string | null;
  pages: number;
  created: string;
  modified: string | null;
  modifiedBy: string | null;
  rpdType: string;
  isPermanentExpire: boolean;
  isKilled: boolean;
  permanentExpiredDate: string | null;
  killDate: string | null;
  pageLevelRestrictionSetting: boolean;
  documentStatus?: string;
  documentCreated?: number;
  documentCreatedFormatted?: string;
  documentCreatedTimeFormatted?: string;
  documentExpiryDate?: number;
  documentExpiryDateFormatted?: string;
  documentExpiryTimeFormatted?: string;
  documentExpiryToolTip?: string;
  documentExpiresSoon?: boolean;
  documentDeadSoon?: boolean;
  documentSecurityLevel?: string;
  securityMode?: {
    label?: string;
  };
  emailDocument?: boolean;
  documentExpiryDateNgbDateStruct?: NgbDateStruct;
  documentMaxDateNgbDateStruct?: NgbDateStruct;
  documentMinDateNgbDateStruct?: NgbDateStruct;
  documentDomainList?: string[];
  documentNumberOfViews?: number;
  respondTo?: string;
}

export interface Distribution {
  email: string;
  id: string;
  enabled: boolean;
}

export interface DocumentOptions {
  recipients: Recipient[];
  pageRestrictions: string[];
  subject: string;
  message: string;
  sideNote: string;
  enabled: boolean;
  openRecord: boolean;
  geolocation: boolean;
  verifyCode: boolean;
  proofOfSending: boolean;
  readNotification: boolean;
  readerIp: boolean;
  readerEmail: boolean;
  readTimes: boolean;
  readTiming: boolean;
  voting: boolean;
  voteSharing: boolean;
  distRestriction: boolean;
  distributionList: boolean;
  domains: string;
  watermark: number;
  maxReads: number;
  steganography: boolean;
  timestamp: boolean;
  print: boolean;
  regions: string[] | null;
  ips: string[];
  startTime: string;
  expiryTime: string;
  allowResponsesTo: string;
  documentTemplateId: string;
  language: string;
  source: string;
  accessControl: string;
  maxViewsPerDocument: number;
  browserLanguage: boolean;
  pageLevelRestriction: boolean;
  isSMS: boolean;
  isEmail: boolean;
  isBoth: boolean;
  isWhatsApp: boolean;
  eDisclosure: boolean;
  maxPrintsPerReader: number;
  maxPrintsPerDocument: number;
}

export interface Recipient {
  email: string;
  id: string;
  code: string;
  readCount: number;
  enabled: boolean;
  vote: number;
  status: string;
  recipientType: string;
  firstName: string;
  lastName: string;
  mobileCountryCode: string;
  mobileNumber: string;
  authType: string;
  reference: string;
  recipientCreateDate: string;
  eDisclosureAgreed: null;
  eDisclosureTime: string;
}

export interface InputFile {
  fileName: string;
  modifiedFileName: string;
  fileSize: string;
  order: number;
}

export interface OpenRecord {
  id: string;
  email: string;
  opened: string;
  ip: string;
  geolocation: Geolocation;
  activityTime: number;
  client: string;
  event: null;
}

export interface Geolocation {
  ip: string;
  network: string;
  version: string;
  city: string;
  region: string;
  region_code: string;
  country: string;
  country_code: string;
  country_name: string;
  country_capital: string;
  country_tld: string;
  country_code_iso3: string;
  continent_code: string;
  postal: string;
  latitude: number;
  longitude: number;
  timezone: string;
  utc_offset: string;
  country_calling_code: string;
  in_eu: boolean;
  currency: string;
  currency_name: string;
  languages: string;
  country_area: number;
  country_population: number;
  asn: string;
  org: string;
}
